import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI } from '../services/api';

const Schedule = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      const data = response.data.projects || response.data || [];
      setProjects(data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch {
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <span className="loading loading-spinner loading-md text-blue-500"></span>
      </div>
    );
  }

  const publishedCount = projects.filter(p => p.published).length;
  const draftCount = projects.length - publishedCount;
  const visibleProjects = showAll ? projects : projects.filter(p => p.published);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Schedule Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">Select a project to manage its rounds and games</p>
        </div>
        {projects.length > 0 && draftCount > 0 && (
          <label className="flex items-center gap-3 cursor-pointer select-none bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm self-start">
            <span className="text-sm font-medium text-slate-700">Show all</span>
            <input
              type="checkbox"
              className="toggle toggle-sm toggle-primary"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />
            {!showAll && (
              <span className="text-xs text-slate-400">{draftCount} hidden</span>
            )}
          </label>
        )}
      </div>

      {error && (
        <div className="alert alert-error py-2 text-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          {error}
        </div>
      )}

      {/* Stats bar */}
      {projects.length > 0 && (
        <div className="flex gap-4">
          <div className="stat bg-base-100 border border-slate-200 rounded-xl px-5 py-3 shadow-sm">
            <div className="stat-title text-xs">Total</div>
            <div className="stat-value text-2xl text-slate-800">{projects.length}</div>
            <div className="stat-desc">Projects</div>
          </div>
          <div className="stat bg-base-100 border border-slate-200 rounded-xl px-5 py-3 shadow-sm">
            <div className="stat-title text-xs">Published</div>
            <div className="stat-value text-2xl text-green-600">{publishedCount}</div>
            <div className="stat-desc">Active</div>
          </div>
          <div className="stat bg-base-100 border border-slate-200 rounded-xl px-5 py-3 shadow-sm">
            <div className="stat-title text-xs">Drafts</div>
            <div className="stat-value text-2xl text-slate-400">{draftCount}</div>
            <div className="stat-desc">Unpublished</div>
          </div>
        </div>
      )}

      {visibleProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {visibleProjects.map((project) => (
            <div key={project._id} className="card bg-base-100 border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-150">
              <div className="card-body p-5">
                <div className="flex items-start justify-between mb-3">
                  <h2 className="font-semibold text-slate-800 text-lg leading-tight">{project.name}</h2>
                  <span className={`badge badge-sm ${project.published ? 'badge-success' : 'badge-ghost'}`}>
                    {project.published ? 'Published' : 'Draft'}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-slate-500 mb-4">
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>
                    Season: {project.sid?.name || '—'}
                  </div>
                  {project.createdAt && (
                    <div className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      {new Date(project.createdAt).toLocaleDateString()}
                    </div>
                  )}
                </div>

                <button
                  className="btn btn-primary btn-sm w-full"
                  onClick={() => navigate(`/schedule/${project._id}`)}
                >
                  View Rounds
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : projects.length > 0 ? (
        <div className="card bg-base-100 border border-slate-200 shadow-sm">
          <div className="card-body items-center text-center py-16">
            <h3 className="font-semibold text-slate-600">No published projects</h3>
            <p className="text-sm text-slate-400 mt-1">
              {draftCount} draft{draftCount !== 1 ? 's' : ''} available — enable &quot;Show all&quot; to view them.
            </p>
            <button
              type="button"
              className="btn btn-outline btn-sm mt-4"
              onClick={() => setShowAll(true)}
            >
              Show all projects
            </button>
          </div>
        </div>
      ) : (
        <div className="card bg-base-100 border border-slate-200 shadow-sm">
          <div className="card-body items-center text-center py-16">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-slate-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            <h3 className="font-semibold text-slate-600">No Projects Found</h3>
            <p className="text-sm text-slate-400 mt-1">Create a project in Settings to get started.</p>
            <button className="btn btn-primary btn-sm mt-4" onClick={() => navigate('/settings')}>
              Go to Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedule;
